import React, { forwardRef, useState, useEffect, useCallback } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import { BaseBlockProps, ChartDataItem } from '../types'
import { BlockWrapper, BlockHeader, BlockToolbar, LoadingState } from '../base'
import { DonutChart } from '../../../Components/Display/charts'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PercentageMatchingBlockProps extends BaseBlockProps {}

interface PieChartConfig {
    id: number | string
    percentage: number
    label: string
    data: ChartDataItem[]
}

interface LegacyPieChartData {
    id?: number | string
    percentage?: number
    total_dossiers?: number
    totals?: number
    color?: string
    title?: string
}

interface MigratedPieChartData {
    id?: number | string
    matched?: number
    total?: number
    label?: string
}

export const PercentageMatchingBlock = forwardRef<
    HTMLDivElement,
    PercentageMatchingBlockProps
>(
    (
        {
            kedo,
            block,
            dashboard,
            onClose,
            onMax,
            onSuccess,
            getBlockHeader,
            dossier,
            link,
        },
        ref
    ) => {
        const [loading, setLoading] = useState(false)
        const [pieCharts, setPieCharts] = useState<PieChartConfig[]>([])

        const fetchData = useCallback(async () => {
            const environment = kedo.env().getEnvironment()
            if (!environment || !block.id) return

            const api = kedo.api()
            const params: Record<string, unknown> = {}

            if (dossier?.id) {
                params.dossier = dossier.id
                params.linkId = link
            }

            setLoading(true)

            try {
                const response = await api.get(
                    `${api.getMyPageBlockEndpoint(block.id)}/data`,
                    { params }
                )

                // The block endpoint returns the chart array directly. Keep
                // support for the wrapped shape as well, since it was used by
                // the first version of the migrated component.
                const responseData = response.data
                const chartData: Array<
                    LegacyPieChartData | MigratedPieChartData
                > = Array.isArray(responseData)
                    ? responseData
                    : Array.isArray(responseData?.pie_charts)
                    ? responseData.pie_charts
                    : []

                const charts = chartData.map((chart, index) => {
                    const isLegacyChart = 'totals' in chart
                    const matched = isLegacyChart
                        ? Number(chart.total_dossiers ?? 0)
                        : Number(chart.matched ?? 0)
                    const total = isLegacyChart
                        ? Number(chart.totals ?? 0)
                        : Number(chart.total ?? 0)
                    const percentage = isLegacyChart
                        ? Number(chart.percentage ?? 0)
                        : total > 0
                        ? Math.round((matched / total) * 100)
                        : 0

                    return {
                        id: chart.id ?? index,
                        percentage,
                        label: isLegacyChart
                            ? chart.title ?? ''
                            : chart.label ?? '',
                        data: [
                            {
                                id: 'matched',
                                label: kedo.t('Matched'),
                                value: matched,
                                color: isLegacyChart ? chart.color : undefined,
                            },
                            {
                                id: 'unmatched',
                                label: kedo.t('Unmatched'),
                                value: Math.max(total - matched, 0),
                                color: isLegacyChart ? '#fff' : undefined,
                            },
                        ],
                    }
                })

                setPieCharts(charts)
            } catch (error) {
                console.error(
                    'Failed to fetch percentage matching data:',
                    error
                )
            } finally {
                setLoading(false)
            }
        }, [kedo, block, dossier, link])

        useEffect(() => {
            fetchData()
        }, [fetchData])

        const scheme = block.settings?.scheme ?? 'nivo'

        return (
            <BlockWrapper
                ref={ref}
                dashboard={dashboard}
                sx={{
                    height: '100%',
                    marginBottom: '30px',
                    minHeight: '300px',
                    minWidth: '300px',
                }}
            >
                <BlockHeader>
                    <BlockToolbar
                        kedo={kedo}
                        block={block}
                        onClose={onClose}
                        onMax={onMax}
                        onSuccess={onSuccess}
                        fetchData={fetchData}
                    />
                    {getBlockHeader?.(block, dossier)}
                </BlockHeader>
                {loading ? (
                    <LoadingState />
                ) : (
                    <Grid container spacing={2} sx={{ p: 2 }}>
                        {pieCharts.map((chart) => (
                            <Grid
                                key={chart.id}
                                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                            >
                                <Box
                                    sx={{
                                        height: 100,
                                        minHeight: 100,
                                        width: '100%',
                                    }}
                                >
                                    <DonutChart
                                        data={chart.data}
                                        percentage={chart.percentage}
                                        colors={
                                            chart.data.some(
                                                (item) => item.color
                                            )
                                                ? { datum: 'data.color' }
                                                : { scheme }
                                        }
                                    />
                                </Box>
                                <Typography
                                    variant="subtitle2"
                                    align="center"
                                    color="text.secondary"
                                >
                                    {chart.label}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </BlockWrapper>
        )
    }
)

PercentageMatchingBlock.displayName = 'PercentageMatchingBlock'
